import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { supabase, isMockMode, isSupabaseMisconfigured, supabaseConfigErrorMessage } from './services/supabaseClient';
import { Analytics } from './services/analytics';
import { UserRole, DatabaseProfile } from './types';
import { ToastProvider, useToast } from './context/ToastContext';
import { BrandingProvider } from './context/BrandingContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useBranding } from './context/BrandingContext';
import { getBlockedModuleToastMessage, resolveModuleAccess } from './services/moduleAccess';
import { initializeFleetTrialForUser } from './services/fleetTrial';
import { isAdminRole, normalizeUserRole } from './utils/roles';

// Regular imports instead of lazy loading to fix Vite 6.x dynamic import issues
import Layout from './components/Layout';
import { BillingDashboard } from './components/BillingDashboard';

import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ResetPassword from './pages/ResetPassword';
import StripeCallback from './pages/StripeCallback';

import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Vehicles from './pages/Vehicles';
import Hires from './pages/Hires';
import Drivers from './pages/Drivers';
import DriverInvoices from './pages/DriverInvoices';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import InternalLeads from './pages/InternalLeads';
import Pricing from './pages/Pricing';

// --- Auth Context ---
interface AuthContextType {
  session: Session | null;
  role: UserRole;
  profile: { fullName: string | null; email: string | null };
  loading: boolean;
  setMockRole: (role: UserRole) => void; // Only for demo purposes
  authError: string | null;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType>({ 
  session: null, 
  role: 'viewer',
  profile: { fullName: null, email: null },
  loading: true,
  setMockRole: () => {},
  authError: null,
  clearAuthError: () => {}
});

export const useAuth = () => useContext(AuthContext);

const isGoogleSignupCompletionRoute = () => {
  const hash = window.location.hash || '';
  return hash.startsWith('#/signup') && hash.includes('google_onboarding=1');
};

// Page Tracker Component
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    Analytics.track('$pageview', { path: location.pathname });
  }, [location]);
  return null;
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>('viewer');
  const [profile, setProfile] = useState<{ fullName: string | null; email: string | null }>({ fullName: null, email: null });
  const [authError, setAuthError] = useState<string | null>(null);
  const { addToast } = useToast();
  const handleSessionSeq = useRef(0);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    // Initialize Analytics
    Analytics.init();

    let mounted = true;
    if (isSupabaseMisconfigured) {
      setAuthError(supabaseConfigErrorMessage);
      setSession(null);
      setRole('viewer');
      setProfile({ fullName: null, email: null });
      setLoading(false);
      return;
    }

    if (isMockMode) {
      const mockUser = {
        id: 'mock-user',
        aud: 'authenticated',
        role: 'authenticated',
        email: 'demo@pcn-guard.com',
        app_metadata: { provider: 'email' },
        user_metadata: { 
          company_name: 'Demo Fleet Ltd',
          role: 'owner' // Default in metadata
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setSession({
        access_token: 'mock-token',
        refresh_token: 'mock-refresh',
        expires_in: 3600,
        token_type: 'bearer',
        user: mockUser
      } as Session);
      
      Analytics.identify('mock-user', { email: 'demo@pcn-guard.com', is_mock: true, role: 'owner' });

      setRole('owner');
      setLoading(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (mounted && loading) setLoading(false);
    }, 3000);

    const handleSession = async (session: Session | null) => {
      const seq = ++handleSessionSeq.current;
      const isStale = () => !mounted || seq !== handleSessionSeq.current;

      console.log('[AuthProvider] handleSession called, session:', session ? 'EXISTS' : 'NULL');
      if (!isStale()) setSession(session);
      if (!session?.user) {
        console.log('[AuthProvider] No session user, clearing state');
        lastUserId.current = null;
        if (!isStale()) {
          setAuthError(null);
          setRole('viewer');
          setProfile({ fullName: null, email: null });
          setLoading(false);
        }
        return;
      }
      if (session?.user) {
        const nextUserId = session.user.id;
        const prevUserId = lastUserId.current;
        if (prevUserId && prevUserId !== nextUserId) {
          const email = session.user.email;
          addToast(`Session changed. Signed in as ${email ?? 'a different user'}.`, 'info');
        }
        lastUserId.current = nextUserId;

        // Source of truth: profiles.role (no profile => block sign-in)
        let userRole: UserRole = 'viewer';
        try {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role, organisation_id, full_name, email')
            .eq('id', session.user.id)
            .limit(1)
            .maybeSingle();
          if (isStale()) return;

          // Check for invalid JWT/session errors
          if (profileError && (
            profileError.message?.includes('invalid claim') ||
            profileError.message?.includes('missing sub claim') ||
            profileError.message?.includes('JWT')
          )) {
            console.warn('Invalid session detected, clearing...');
            if (!isStale()) setAuthError('Your session has expired. Please sign in again.');
            await supabase.auth.signOut({ scope: 'local' });
            if (!isStale()) {
              setSession(null);
              setRole('viewer');
              setLoading(false);
            }
            return;
          }

          let effectiveProfile = profile;

          if (profileError || !profile?.organisation_id) {
            try {
              await supabase.rpc('ensure_user_has_org');
              const { data: backfilledProfile, error: backfillError } = await supabase
                .from('profiles')
                .select('role, organisation_id, full_name, email')
                .eq('id', session.user.id)
                .limit(1)
                .maybeSingle();
              if (backfillError || !backfilledProfile) throw backfillError || new Error('Profile not found after backfill');
              effectiveProfile = backfilledProfile;
            } catch (backfillError) {
              console.error('[AuthProvider] Profile backfill failed:', backfillError);
              if (isGoogleSignupCompletionRoute()) {
                if (!isStale()) {
                  setAuthError(null);
                  setRole('viewer');
                  setProfile({
                    fullName: String(session.user.user_metadata?.full_name || session.user.user_metadata?.name || '').trim() || null,
                    email: String(session.user.email || '').trim() || null,
                  });
                  setLoading(false);
                }
                return;
              }
              if (!isStale()) setAuthError('Account setup incomplete. Please sign up first.');
              await supabase.auth.signOut({ scope: 'local' });
              if (!isStale()) {
                setSession(null);
                setRole('viewer');
                setProfile({ fullName: null, email: null });
                setLoading(false);
              }
              return;
            }
          }
          // Ensure the org always has an admin-capable member after legacy backfills.
          try {
            await supabase.rpc('ensure_org_has_admin');
          } catch {
            // Ignore errors from ensure_org_has_admin
          }
          const { data: refreshedProfile } = await supabase
            .from('profiles')
            .select('role, full_name, email')
            .eq('id', session.user.id)
            .limit(1)
            .maybeSingle();
          if (isStale()) return;

          const effectiveRole = refreshedProfile?.role ?? effectiveProfile?.role;
          userRole = normalizeUserRole(effectiveRole ?? session.user.user_metadata?.role);

          // Type-safe profile data handling
          const typedRefreshedProfile = refreshedProfile as DatabaseProfile | null;
          const typedProfile = effectiveProfile as DatabaseProfile | null;

          const effectiveFullName =
            String(typedRefreshedProfile?.full_name || typedProfile?.full_name || '').trim() ||
            String(session.user.user_metadata?.full_name || session.user.user_metadata?.name || '').trim() ||
            null;

          const effectiveEmail =
            String(typedRefreshedProfile?.email || typedProfile?.email || session.user.email || '').trim() || null;

          if (!isStale()) setProfile({ fullName: effectiveFullName, email: effectiveEmail });
          if (!isStale()) setAuthError(null);

          try {
            await initializeFleetTrialForUser(session.user);
          } catch (trialError) {
            console.error('[AuthProvider] FleetMode trial initialization failed:', trialError);
          }
        } catch (err: any) {
          // Check for invalid JWT/session errors
          if (err?.message?.includes('invalid claim') ||
              err?.message?.includes('missing sub claim') ||
              err?.message?.includes('JWT')) {
            console.warn('Invalid session detected, clearing...');
            if (!isStale()) setAuthError('Your session has expired. Please sign in again.');
            await supabase.auth.signOut({ scope: 'local' });
            if (!isStale()) {
              setSession(null);
              setRole('viewer');
              setLoading(false);
            }
            return;
          }

          console.error('[AuthProvider] Error in handleSession:', err);
          if (!isStale()) setAuthError('Account setup incomplete. Please sign up first.');
          await supabase.auth.signOut({ scope: 'local' });
          if (!isStale()) {
            setSession(null);
            setRole('viewer');
            setProfile({ fullName: null, email: null });
            setLoading(false);
          }
          return;
        }

        if (!isStale()) {
          setRole(userRole);
          Analytics.identify(session.user.id, { email: session.user.email, role: userRole });
        }
      }
    };

    supabase.auth.getSession()
      .then(async ({ data: { session }, error }) => {
        // If there's an error getting the session (e.g., corrupted JWT), clear it
        if (error && (
          error.message?.includes('invalid claim') ||
          error.message?.includes('missing sub claim') ||
          error.message?.includes('JWT')
        )) {
          console.warn('Corrupted session detected on mount, clearing...');
          supabase.auth.signOut({ scope: 'local' });
          if (mounted) {
            setSession(null);
            setAuthError('Your session has expired. Please sign in again.');
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          await handleSession(session);
          setLoading(false);
        }
      })
      .catch((err) => {
        // Catch any unexpected errors during session retrieval
        if (err?.message?.includes('invalid claim') ||
            err?.message?.includes('missing sub claim') ||
            err?.message?.includes('JWT')) {
          console.warn('Error getting session, clearing...');
          supabase.auth.signOut({ scope: 'local' });
          if (mounted) {
            setSession(null);
            setAuthError('Your session has expired. Please sign in again.');
            setLoading(false);
          }
        }
      })
      .finally(() => { clearTimeout(timeoutId); });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) { 
        void handleSession(session);
        setLoading(false);
      }
    });

    return () => { mounted = false; clearTimeout(timeoutId); subscription.unsubscribe(); };
  }, [addToast]);

  const setMockRole = (newRole: UserRole) => {
    if (isMockMode) setRole(newRole);
  };

  const clearAuthError = () => setAuthError(null);

  return (
    <AuthContext.Provider value={{ session, role, profile, loading, setMockRole, authError, clearAuthError }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// --- Root Route (App only - no marketing pages) ---
const RootRoute: React.FC = () => {
  const { session, loading } = useAuth();

  console.log('[RootRoute] session:', session ? 'EXISTS' : 'NULL', 'loading:', loading);

  // Show loading state while checking session
  if (loading) {
    console.log('[RootRoute] Showing loading screen');
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400">
        Loading...
      </div>
    );
  }

  // Redirect to login if no session
  if (!session) {
    console.log('[RootRoute] No session, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Show dashboard if authenticated
  console.log('[RootRoute] Session exists, showing Dashboard');
  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
};

// --- Auth Guard ---
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, loading } = useAuth();
  if (loading) return <div className="h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400">Loading...</div>;
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// --- Role Guard ---
const RoleGuard: React.FC<{ children: React.ReactNode; requiredRole: UserRole }> = ({ children, requiredRole }) => {
  const { role } = useAuth();
  if (isAdminRole(requiredRole) && !isAdminRole(role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const ModuleRouteGuard: React.FC<{
  children: React.ReactNode;
  module: 'hires' | 'driverInvoices';
}> = ({ children, module }) => {
  const location = useLocation();
  const { role } = useAuth();
  const { organization } = useBranding();
  const { addToast } = useToast();

  const access = resolveModuleAccess({ organization, role });
  const blocked = !access.canAccessModule(module);

  useEffect(() => {
    if (!blocked) return;
    addToast(getBlockedModuleToastMessage(), 'info');
  }, [addToast, blocked, location.pathname, module]);

  if (blocked) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// --- Main App ---
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <HashRouter>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <BrandingProvider>
                  <PageTracker />
	                  <Routes>
                    <Route path="/" element={<RootRoute />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Note: Marketing pages (privacy, terms) are on the main website: pcnguard.co.uk */}

	                  <Route path="/stripe-callback" element={
	                    <ProtectedRoute>
	                      <StripeCallback />
	                    </ProtectedRoute>
	                  } />

	                  {/* Public or Auth-Protected Checkout Callbacks */}
	                  <Route path="/checkout/*" element={
	                    <ProtectedRoute>
	                      <StripeCallback />
                    </ProtectedRoute>
                  } />

                  <Route path="/*" element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                            {/* Dashboard Route */}
                            <Route path="/dashboard" element={<Dashboard />} />

                            {/* Common Routes */}
                            <Route path="/tasks" element={<Tasks />} />
                            <Route path="/tickets" element={<Tickets />} />
                            <Route path="/tickets/:id" element={<TicketDetail />} />
                            <Route path="/vehicles" element={<Vehicles />} />
                            <Route
                              path="/hires"
                              element={
                                <ModuleRouteGuard module="hires">
                                  <Hires />
                                </ModuleRouteGuard>
                              }
                            />
                            <Route path="/drivers" element={<Drivers />} />
                            <Route path="/trial-report" element={<Navigate to="/reports?tab=trial-snapshot" replace />} />
                            <Route
                              path="/driver-invoices"
                              element={
                                <ModuleRouteGuard module="driverInvoices">
                                  <DriverInvoices />
                                </ModuleRouteGuard>
                              }
                            />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/internal/leads" element={<InternalLeads />} />

                            {/* Admin Only Routes */}
                            <Route
                              path="/reports"
                              element={
                                <RoleGuard requiredRole="admin">
                                  <Reports />
                                </RoleGuard>
                              }
                            />

                            <Route
                              path="/pricing"
                              element={
                                <RoleGuard requiredRole="admin">
                                  <Pricing />
                                </RoleGuard>
                              }
                            />

                            <Route
                              path="/billing"
                              element={
                                <RoleGuard requiredRole="admin">
                                  <BillingDashboard />
                                </RoleGuard>
                              }
                            />

                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </Layout>
                    </ProtectedRoute>
                  } />
                </Routes>
                </BrandingProvider>
              </SubscriptionProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </HashRouter>
    </ErrorBoundary>
  );
};

export default App;
